import type { FC } from 'react';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm } from 'react-final-form';
import useDebounce from 'react-use/lib/useDebounce';
import { StaticActions } from '@alfa-bank/site-form-utils/actions';
import type { IDropdown } from '@alfa-bank/site-form-utils/src/components/base-select/models';

import { dataFieldName, getSuggestionAddress } from '../helpers';
import type { IFormattedSuggestionData, ISuggestions } from '../models';
import { DEBOUNCE_DELAY } from '../config';

import type { TAddressRegistrationInput } from './models';

export const AddressRegistrationInput: FC<TAddressRegistrationInput> = ({
    Select,
    className,
    disabled,
    getCaption,
    input,
    isError,
    isSuccess,
    meta,
    name,
    onSelect,
    settings,
}) => {
    const formApi = useForm();

    const [suggestions, setSuggestions] = useState<ISuggestions[]>([]);
    const [addressObj, setAddressObj] = useState<IFormattedSuggestionData | undefined>(
        () => formApi?.getState()?.initialValues?.[dataFieldName(name)]
    );

    const error = useMemo(() => isError(meta), [isError, meta]);
    const success = useMemo(() => isSuccess(meta), [isSuccess, meta]);
    const caption = useMemo(
        () => getCaption(meta, settings?.caption),
        [meta, settings, getCaption]
    );

    const setValue = useCallback(
        (value: any) => {
            StaticActions.onChange(input)(value);
        },
        [input]
    );

    const setAddress = useCallback(
        (selected: any) => {
            formApi.change(dataFieldName(name), selected?.data || undefined);
            setAddressObj(selected?.data || null);
        },
        [formApi, name]
    );

    const handleBlur = useCallback(() => {
        input.onBlur();
        StaticActions.onBlur(input)();
    }, [input]);

    const handleFocus = useCallback(() => {
        input.onFocus();
        StaticActions.onFocus(input);
    }, [input]);

    const handleSelect = useCallback(
        (selected: IDropdown) => {
            setValue(selected.value);
            setAddress(selected);
            StaticActions.onSelect({ input, meta })(selected.value);
            StaticActions.onBlur(input)();
            if (onSelect) {
                // @ts-ignore Тут забагованные типы у BaseSelect
                onSelect(selected.value, selected?.data, formApi?.getFieldState(name)?.valid);
            }
        },
        [setValue, setAddress, input, meta, onSelect, formApi, name]
    );

    const handleCancel = useCallback(
        () =>
            StaticActions.onCancel({
                input,
                meta,
            }),
        [input, meta]
    );

    const handleChange = useCallback(
        ({ value }: any) => {
            setValue(value);
        },
        [setValue]
    );

    useDebounce(
        async () => {
            const res = await getSuggestionAddress(input.value);
            setSuggestions(res);
        },
        DEBOUNCE_DELAY,
        [input.value]
    );

    useEffect(() => {
        if (input.value !== addressObj?.fullName) {
            setAddress(null);
        }
    }, [addressObj?.fullName, formApi, input.value, name, setAddress]);

    return (
        <div className={className}>
            <Select
                {...input}
                caption={caption}
                disabled={disabled}
                error={error}
                forwardedRef={StaticActions.getRef(meta)}
                name={name}
                onBlur={handleBlur}
                onCancel={handleCancel}
                onChange={handleChange}
                onFocus={handleFocus}
                onSelect={handleSelect}
                options={suggestions}
                placeholder={settings?.placeholder}
                success={success}
                type="text"
                value={input.value}
            />
        </div>
    );
};
